// ToolResultCards — #242. Renders Link's READ-tool results as rich entity cards
// instead of raw JSON/markdown tables. The agent's tool result string arrives on
// the `tool` thread entry (forwarded via nexilinkAgentTransport `tool_end.result`);
// ChatThread delegates the recognised tools here.
//
// Only the structured-JSON list/stat tools are carded. get_need/get_module return
// markdown and drive the artifact preview pane instead, so they're intentionally
// absent. All internal ids are omitted from display (#224).

import React from 'react';
import {
    Users, UserSearch, BarChart3, ListTodo, Megaphone, GraduationCap,
    MapPin, Clock, Building2, Building, Sparkles, User, Mail, Phone,
    Hash, Briefcase,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { IconChip } from '@/components/custom-ui/IconChip';
import { UserAvatar } from '@/components/custom-ui/UserAvatar';

// Tools whose results render as a rich card. Exported so the dev "Verktygsanrop"
// toggle can keep showing these even when the raw tool-log rows are hidden.
//
// Two flavours live here:
//  - "read-only" list/stat tools (get_org_stats, list_*) — the card IS the
//    deliverable the user asked to see.
//  - CONTEXT tools (get_user_profile / get_organisation_info / get_unit_info) —
//    these are NOT read-only in effect: Link fetches them mid-authoring and uses
//    the values to personalise, pre-fill forms and sign modules/needs on the
//    user's behalf. Their cards are framed as a compact "context Link now holds
//    and will act on" confirmation (with a footer note), not a data report.
export const RICH_CARD_TOOLS = new Set<string>([
    'get_org_stats',
    'list_employees',
    'list_applicants',
    'list_pending_actions',
    'list_employer_needs',
    'list_employer_modules',
    'list_capabilities',
    // context tools (see note above)
    'get_user_profile',
    'get_organisation_info',
    'get_unit_info',
]);

// CONVERSATIONAL tools — real user-facing exchanges (a question asked + answered,
// a plan proposed + decided), not debug noise. They render AS conversation and are
// NEVER hidden by the "Verktygsanrop" toggle — hiding them makes the chat read as
// Link talking to himself (the Q&A / plan+verdict vanish).
export const CONVERSATIONAL_TOOLS = new Set<string>(['ask_user', 'plan_ready']);

// ACTION tools — tools that DID something with real-world effect on the user's
// behalf (invited people, decided an application, published a posting, edited a
// draft). Their outcome is an EVENT in the conversation, so they render as a
// compact "receipt" and also survive the toggle: hiding them would drop the record
// of what Link actually did (an audit-trail concern, not just cosmetics).
export const ACTION_TOOLS = new Set<string>([
    'invite_employees',
    'decide_application',
    'assign_onboarding',
    'set_posting_status',
    'attach_courses_to_posting',
    'emit_artifact',
    'update_artifact',
    'delete_artifact',
    'delete_artifacts',
    'save_skill',
    'save_memory',
]);

// A tool row stays visible when the "Verktygsanrop" (show tool-calls) toggle is
// OFF only when it is NOT debug-read noise: a rich data card the user asked for,
// a conversational gate, or a consequential action. Everything else (read_source_*,
// fetch_url, current_time, list_* reads, …) is genuine "how Link worked it out"
// noise and stays hidden.
export const alwaysVisibleTool = (name?: string | null): boolean =>
    !!name && (RICH_CARD_TOOLS.has(name) || CONVERSATIONAL_TOOLS.has(name) || ACTION_TOOLS.has(name));

// Cards live inside a chat bubble. Short lists render in full; once a list grows
// past this many rows the row container becomes a fixed-height scroll area so the
// card stays compact but every row is still reachable.
const SCROLL_AFTER = 8;
const listClass = (n: number): string =>
    `space-y-1.5${n > SCROLL_AFTER ? ' max-h-80 overflow-y-auto pr-1 -mr-1' : ''}`;

function safeParse(result: unknown): any {
    if (result == null) return null;
    if (typeof result === 'object') return result;
    if (typeof result !== 'string') return null;
    try {
        return JSON.parse(result);
    } catch {
        return null;
    }
}

// ── small shared bits ──────────────────────────────────────────────
const Card: React.FC<{ icon: LucideIcon; title: string; count?: number; children: React.ReactNode }> = ({
    icon: Icon, title, count, children,
}) => (
    <div className="p-3 bg-white border border-gray-200 rounded-xl space-y-2.5 shadow-sm">
        <div className="flex items-center gap-2">
            <IconChip icon={Icon} tone="brand" size="sm" />
            <span className="text-sm font-semibold text-gray-900">{title}</span>
            {count != null && (
                <span className="ml-auto text-xs font-medium text-gray-400">{count} st</span>
            )}
        </div>
        {children}
    </div>
);

const Badge: React.FC<{ tone: 'green' | 'blue' | 'amber' | 'red' | 'gray'; children: React.ReactNode }> = ({ tone, children }) => {
    const tones: Record<string, string> = {
        green: 'bg-green-50 text-green-700 border-green-200',
        blue: 'bg-blue-50 text-blue-700 border-blue-200',
        amber: 'bg-amber-50 text-amber-700 border-amber-200',
        red: 'bg-red-50 text-red-700 border-red-200',
        gray: 'bg-gray-100 text-gray-600 border-gray-200',
    };
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${tones[tone]}`}>
            {children}
        </span>
    );
};

const onboardingBadge = (status: string, progress?: string) => {
    const map: Record<string, { tone: 'green' | 'blue' | 'gray'; label: string }> = {
        completed: { tone: 'green', label: 'Klar' },
        in_progress: { tone: 'blue', label: 'Pågår' },
        not_started: { tone: 'gray', label: 'Ej påbörjad' },
        none: { tone: 'gray', label: 'Ingen onboarding' },
    };
    const b = map[status] ?? map.none;
    return <Badge tone={b.tone}>{b.label}{progress ? ` · ${progress}` : ''}</Badge>;
};

const applicationBadge = (status: string) => {
    const map: Record<string, { tone: 'green' | 'blue' | 'amber' | 'red' | 'gray'; label: string }> = {
        pending: { tone: 'amber', label: 'Väntar' },
        reviewing: { tone: 'blue', label: 'Granskas' },
        accepted: { tone: 'green', label: 'Antagen' },
        rejected: { tone: 'red', label: 'Avslagen' },
        withdrawn: { tone: 'gray', label: 'Tillbakadragen' },
    };
    const b = map[status] ?? { tone: 'gray' as const, label: status };
    return <Badge tone={b.tone}>{b.label}</Badge>;
};

const needBadge = (status: string) => {
    const map: Record<string, { tone: 'green' | 'blue' | 'gray'; label: string }> = {
        active: { tone: 'green', label: 'Publicerad' },
        paused: { tone: 'gray', label: 'Pausad' },
        filled: { tone: 'blue', label: 'Tillsatt' },
        closed: { tone: 'gray', label: 'Stängd' },
        draft: { tone: 'gray', label: 'Utkast' },
    };
    const b = map[status] ?? { tone: 'gray' as const, label: status };
    return <Badge tone={b.tone}>{b.label}</Badge>;
};

// ── per-tool cards ─────────────────────────────────────────────────
const StatTile: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <div className="p-2 bg-gray-50 rounded-lg">
        <p className="text-lg font-bold text-gray-900 leading-none">{value}</p>
        <p className="text-[11px] text-gray-500 mt-1">{label}</p>
    </div>
);

const OrgStatsCard: React.FC<{ data: any }> = ({ data }) => {
    const ob = data.onboarding ?? {};
    return (
        <Card icon={BarChart3} title="Översikt">
            <div className="grid grid-cols-3 gap-2">
                <StatTile label="Medarbetare" value={data.employees ?? 0} />
                <StatTile label="Enheter" value={data.units ?? 0} />
                <StatTile label="Publicerade annonser" value={data.active_postings ?? 0} />
                <StatTile label="Moduler" value={data.courses ?? 0} />
                <StatTile label="Väntande inbjudn." value={data.pending_invitations ?? 0} />
                <StatTile label="Sökande" value={data.applicants_total ?? 0} />
            </div>
            {(ob.completed != null || ob.in_progress != null || ob.not_started != null) && (
                <div className="flex flex-wrap gap-1.5 pt-0.5">
                    <Badge tone="green">Klar {ob.completed ?? 0}</Badge>
                    <Badge tone="blue">Pågår {ob.in_progress ?? 0}</Badge>
                    <Badge tone="gray">Ej påbörjad {ob.not_started ?? 0}</Badge>
                    {ob.no_program != null && <Badge tone="gray">Utan program {ob.no_program}</Badge>}
                </div>
            )}
        </Card>
    );
};

const EmployeesCard: React.FC<{ data: any }> = ({ data }) => {
    const employees: any[] = Array.isArray(data.employees) ? data.employees : [];
    const scope = data.scope === 'unit' ? 'Enheten' : 'Organisationen';
    return (
        <Card icon={Users} title={`Medarbetare · ${scope}`} count={data.count ?? employees.length}>
            <div className={listClass(employees.length)}>
                {employees.map((e, i) => (
                    <div key={i} className="flex items-center gap-2.5 p-2 bg-gray-50 rounded-lg">
                        <UserAvatar name={e.name || e.email || 'Medarbetare'} size="sm" />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">{e.name || e.email || 'Medarbetare'}</p>
                            {e.unit && (
                                <p className="text-[11px] text-gray-400 flex items-center gap-1 truncate">
                                    <Building2 className="w-3 h-3 flex-shrink-0" /> {e.unit}
                                </p>
                            )}
                        </div>
                        {onboardingBadge(e.onboarding_status, e.onboarding_progress)}
                    </div>
                ))}
            </div>
        </Card>
    );
};

const ApplicantsCard: React.FC<{ data: any }> = ({ data }) => {
    const applicants: any[] = Array.isArray(data.applicants) ? data.applicants : [];
    return (
        <Card icon={UserSearch} title="Sökande" count={data.count ?? applicants.length}>
            <div className={listClass(applicants.length)}>
                {applicants.map((a, i) => (
                    <div key={i} className="flex items-center gap-2.5 p-2 bg-gray-50 rounded-lg">
                        <UserAvatar name={a.applicant || 'Sökande'} size="sm" />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">{a.applicant || 'Sökande'}</p>
                            <p className="text-[11px] text-gray-400 truncate">{[a.role, a.posting].filter(Boolean).join(' · ')}</p>
                        </div>
                        {applicationBadge(a.status)}
                    </div>
                ))}
            </div>
        </Card>
    );
};

const PendingActionsCard: React.FC<{ data: any }> = ({ data }) => {
    const actions: any[] = Array.isArray(data.actions) ? data.actions : [];
    if (actions.length === 0) {
        return (
            <Card icon={ListTodo} title="Att göra">
                <p className="text-sm text-gray-500">Inget som behöver din uppmärksamhet just nu.</p>
            </Card>
        );
    }
    return (
        <Card icon={ListTodo} title="Att göra" count={data.count ?? actions.length}>
            <div className={listClass(actions.length)}>
                {actions.map((a, i) => (
                    <div key={i} className="flex items-start gap-2.5 p-2 bg-gray-50 rounded-lg">
                        <span className="mt-0.5"><Badge tone="amber">{a.count}</Badge></span>
                        <p className="text-sm text-gray-800 flex-1">{a.suggestion}</p>
                    </div>
                ))}
            </div>
        </Card>
    );
};

const NeedsCard: React.FC<{ data: any }> = ({ data }) => {
    const needs: any[] = Array.isArray(data.annonser) ? data.annonser : [];
    return (
        <Card icon={Megaphone} title="Annonser" count={data.count ?? needs.length}>
            <div className={listClass(needs.length)}>
                {needs.map((n, i) => (
                    <div key={i} className="flex items-center gap-2.5 p-2 bg-gray-50 rounded-lg">
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">{n.title || n.role || 'Annons'}</p>
                            {n.city && (
                                <p className="text-[11px] text-gray-400 flex items-center gap-1 truncate">
                                    <MapPin className="w-3 h-3 flex-shrink-0" /> {n.city}
                                </p>
                            )}
                        </div>
                        {n.status && needBadge(n.status)}
                    </div>
                ))}
            </div>
        </Card>
    );
};

const ModulesCard: React.FC<{ data: any }> = ({ data }) => {
    const modules: any[] = Array.isArray(data.modules) ? data.modules : [];
    return (
        <Card icon={GraduationCap} title="Moduler" count={data.count ?? modules.length}>
            <div className={listClass(modules.length)}>
                {modules.map((m, i) => (
                    <div key={i} className="flex items-center gap-2.5 p-2 bg-gray-50 rounded-lg">
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">{m.name || 'Modul'}</p>
                            {m.description && <p className="text-[11px] text-gray-400 truncate">{m.description}</p>}
                        </div>
                        {m.estimated_duration && (
                            <span className="text-[11px] text-gray-400 flex items-center gap-1 flex-shrink-0">
                                <Clock className="w-3 h-3" /> {m.estimated_duration}
                            </span>
                        )}
                    </div>
                ))}
            </div>
        </Card>
    );
};

// onPick — when provided (live chat), each capability row becomes a button that
// injects its starter prompt into the composer (it does NOT auto-send). When
// omitted (read-only history), rows render as plain, non-interactive tiles.
const CapabilitiesCard: React.FC<{ data: any; onPick?: (text: string) => void }> = ({ data, onPick }) => {
    const groups: any[] = Array.isArray(data.groups) ? data.groups : [];
    return (
        <Card icon={Sparkles} title="Det här kan jag hjälpa dig med">
            <div className="space-y-3">
                {groups.map((g, gi) => {
                    const items: any[] = Array.isArray(g.items) ? g.items : [];
                    return (
                        <div key={gi} className="space-y-1.5">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{g.title}</p>
                            <div className="space-y-1">
                                {items.map((it, ii) => {
                                    const body = (
                                        <>
                                            <p className="text-sm font-medium text-gray-900">{it.name}</p>
                                            {it.desc && <p className="text-[12px] text-gray-500 mt-0.5 leading-snug">{it.desc}</p>}
                                        </>
                                    );
                                    const starter = it.prompt || it.name;
                                    return onPick ? (
                                        <button
                                            key={ii}
                                            type="button"
                                            onClick={() => onPick(String(starter))}
                                            className="w-full text-left p-2 bg-gray-50 hover:bg-[#A4C3B2]/20 rounded-lg transition-colors"
                                        >
                                            {body}
                                        </button>
                                    ) : (
                                        <div key={ii} className="p-2 bg-gray-50 rounded-lg">{body}</div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </Card>
    );
};

// ── context cards (get_user_profile / get_organisation_info / get_unit_info) ──
// Compact, transparency-framed cards. These tools feed the agent context it then
// ACTS on (personalising copy, pre-filling forms, signing modules/needs), so the
// card's job is to show the user *what Link now knows and will use*, not to be a
// data report. Empty fields are dropped so the card stays tight.
const InfoRow: React.FC<{ icon: LucideIcon; value?: string }> = ({ icon: Icon, value }) => {
    if (!value || !value.trim()) return null;
    return (
        <div className="flex items-center gap-2 text-[13px] text-gray-700">
            <Icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <span className="truncate">{value}</span>
        </div>
    );
};

// Footer note that makes the "not read-only" nature explicit to the user.
const ContextNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <p className="text-[11px] text-gray-400 leading-snug pt-0.5 border-t border-gray-100">
        {children}
    </p>
);

// approved — for the gated get_user_profile, whether the call actually ran (i.e.
// the user approved). Drives the consent verdict line, folding in what the old
// ChatThread special-case used to show on its own.
const ProfileCard: React.FC<{ data: any; approved?: boolean }> = ({ data, approved }) => {
    const name = String(data.name || '').trim() || 'Din profil';
    return (
        <Card icon={User} title="Din profil">
            <div className="flex items-center gap-2.5">
                <UserAvatar name={name} size="sm" />
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                    {(data.role || data.job_title) && (
                        <p className="text-[11px] text-gray-400 truncate">
                            {[data.job_title, data.role].filter(Boolean).join(' · ')}
                        </p>
                    )}
                </div>
                {approved && <Badge tone="green">Godkänd</Badge>}
            </div>
            <div className="space-y-1.5">
                <InfoRow icon={Briefcase} value={data.job_title} />
                <InfoRow icon={Mail} value={data.email} />
            </div>
            <ContextNote>Link kan använda dina uppgifter för att personifiera och signera innehåll åt dig.</ContextNote>
        </Card>
    );
};

const OrgInfoCard: React.FC<{ data: any }> = ({ data }) => {
    const address = [data.address, [data.postal_code, data.city].filter(Boolean).join(' ')]
        .filter(Boolean).join(', ');
    return (
        <Card icon={Building2} title="Organisation">
            <p className="text-sm font-semibold text-gray-900">{data.name || 'Organisation'}</p>
            <div className="space-y-1.5">
                <InfoRow icon={Hash} value={data.org_number} />
                <InfoRow icon={User} value={data.contact_name} />
                <InfoRow icon={Mail} value={data.contact_email} />
                <InfoRow icon={Phone} value={data.contact_phone} />
                <InfoRow icon={MapPin} value={address} />
            </div>
            <ContextNote>Link kan använda företagsuppgifterna för att fylla i och rama in innehåll.</ContextNote>
        </Card>
    );
};

const UnitInfoCard: React.FC<{ data: any }> = ({ data }) => (
    <Card icon={Building} title="Enhet">
        <p className="text-sm font-semibold text-gray-900">{data.name || 'Enhet'}</p>
        {data.description && (
            <p className="text-[12px] text-gray-500 leading-snug">{data.description}</p>
        )}
        <div className="space-y-1.5">
            <InfoRow icon={MapPin} value={data.city} />
            <InfoRow icon={User} value={data.contact_name} />
            <InfoRow icon={Mail} value={data.contact_email} />
            <InfoRow icon={Phone} value={data.contact_phone} />
        </div>
        <ContextNote>Link kan använda enhetens uppgifter för att anpassa innehåll till teamet.</ContextNote>
    </Card>
);

// toolResultCard — the card element for a recognised tool, or null (so the caller
// falls back to the generic tool-log row) when the tool isn't carded, the result
// hasn't arrived yet (tool still running), or it can't be parsed into the
// expected shape. Returns a React element built from card components (which own
// their own hooks) — safe to render from the caller's JSX.
export function toolResultCard(
    name: string,
    result?: unknown,
    onCapabilityPick?: (text: string) => void,
): React.ReactElement | null {
    if (!RICH_CARD_TOOLS.has(name)) return null;
    const data = safeParse(result);
    if (!data || typeof data !== 'object') return null;
    switch (name) {
        case 'get_org_stats': return <OrgStatsCard data={data} />;
        case 'list_employees': return <EmployeesCard data={data} />;
        case 'list_applicants': return <ApplicantsCard data={data} />;
        case 'list_pending_actions': return <PendingActionsCard data={data} />;
        case 'list_employer_needs': return <NeedsCard data={data} />;
        case 'list_employer_modules': return <ModulesCard data={data} />;
        case 'list_capabilities': return <CapabilitiesCard data={data} onPick={onCapabilityPick} />;
        case 'get_user_profile': return <ProfileCard data={data} approved />;
        case 'get_organisation_info': return <OrgInfoCard data={data} />;
        case 'get_unit_info': return <UnitInfoCard data={data} />;
        default: return null;
    }
}
